import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  Renderer2,
  Inject,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import * as d3 from 'd3';
import { interval, Subscription } from 'rxjs';
import moment from 'moment';
import { DOCUMENT } from '@angular/common';
import { CoreService } from '../../../../core/services/core.service';
import { SessionOptionsService } from '../../../../core/services/session-option.service';
import { TimelineService } from '../../../../core/services/timeline.service';
import { WindowService } from '../../../../core/services/window.service';
import { CoresService } from '../../../../core/services/cores.service';

@Component({
  selector: 'app-playback-timeline',
  standalone: true,
  imports: [],
  templateUrl: './playback-timeline.component.html',
  styleUrls: ['./playback-timeline.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PlaybackTimelineComponent implements OnInit, OnDestroy {
  @ViewChild('d3Timeline', { static: true }) d3Timeline!: ElementRef;
 private subscriptions: Subscription[] = [];
  // UI state
  play = false;
  speed = 1;
  currentDate = 'Click Start to Start Playback';
  goToStartClicked = false;

  // D3 and timeline state
  private readonly MAX_STEPS = 4;
  private readonly MIN_ZOOM = -25;
  private readonly MAX_ZOOM = 3;
  private margin = { top: 0, right: 0, bottom: 12, left: 0 };
  private width!: number;
  private height = 60 - this.margin.top - this.margin.bottom;
  private middleTimePixelX!: number;
  private x0!: d3.ScaleTime<number, number>;
  private x!: d3.ScaleTime<number, number>;
  private xAxis!: d3.Axis<Date>;
  private zoomBehavior!: d3.ZoomBehavior<SVGSVGElement, unknown>;
  private svg!: d3.Selection<SVGGElement, unknown, null, undefined>;
  private svgElement!: d3.Selection<SVGSVGElement, unknown, null, undefined>;
  private timeline!: d3.Selection<SVGGElement, unknown, null, undefined>;
  private currentTransform: d3.ZoomTransform = d3.zoomIdentity;
  private zoomLevel = 0;
  private middleMili = Date.now();
  private goingToLive = false;
  private intervalSubscription!: Subscription;
  private pauseSubscription!: Subscription;
  private startSubscription!: Subscription;
  private isInternalUpdate = false;

  constructor(
    private timelineService: TimelineService,
    private core: CoresService,
    private sessionOptionsService: SessionOptionsService,
    private windowService: WindowService,
    private renderer: Renderer2,
    private cdr: ChangeDetectorRef,
    @Inject(DOCUMENT) private document: Document
  ) {}

  ngOnInit() {
    this.initializeTimeline();
    this.startTimeInterval();
    this.setupEventListeners();
  }

  ngOnDestroy() {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    
    if (this.intervalSubscription) {
      this.intervalSubscription.unsubscribe();
    }
    if (this.pauseSubscription) {
      this.pauseSubscription.unsubscribe();
    }
    if (this.startSubscription) {
      this.startSubscription.unsubscribe();
    }
  }


  private initializeTimeline() {
    this.width = window.innerWidth;
    this.middleTimePixelX = this.width / 2;

    const currentDate = new Date();
    this.x0 = d3
      .scaleTime<number, number>()
      .domain([currentDate.getTime() - 120000, currentDate.getTime() + 120000])
      .range([0, this.width]);
    this.x = this.x0.copy();

    this.xAxis = d3.axisBottom<Date>(this.x).ticks(d3.timeSecond.every(5)).tickSize(-this.height);

    this.zoomBehavior = d3
      .zoom<SVGSVGElement, unknown>()
      .scaleExtent([1, 1])
      .on('zoom', (event) => {
        this.currentTransform = event.transform;
        this.x.domain(this.x0.range().map(x => this.currentTransform.rescaleX(this.x0).invert(x)));
        this.refresh();
      });

    this.svgElement = d3
      .select(this.d3Timeline.nativeElement)
      .append('svg')
      .attr('width', this.width + this.margin.left + this.margin.right)
      .attr('height', this.height + this.margin.top + this.margin.bottom)
      .style('background-color', '#1a1a1a');
    this.svg = this.svgElement
      .append('g')
      .attr('transform', `translate(${this.margin.left},${this.margin.top})`)
      .on('wheel.zoom', (event) => this.onMouseWheel(event))
      .on('touchmove.zoom', (event) => this.onTouchMove(event));

    this.svgElement.call(this.zoomBehavior);
    this.svg
      .append('rect')
      .attr('width', this.width)
      .attr('height', this.height)
      .attr('fill', '#1a1a1a');

    this.timeline = this.svg
      .append('g')
      .attr('id', 'timeline')
      .attr('fill', '#cccccc')
      .attr('class', 'x axis')
      .attr('transform', `translate(0,${this.height})`)
      .call(this.xAxis);

    this.svg
      .append('rect')
      .attr('width', 1)
      .attr('height', 100)
      .attr('transform', `translate(${this.middleTimePixelX},0)`)
      .attr('fill', 'white');

    this.resetTimeline();
    
    const sessionData = this.sessionOptionsService.getSessionData();
    if (sessionData?.event?.createdAt) {
      const start = moment(sessionData.event.createdAt).toDate();
      this.goToTime(start);
    }
  }

  private startTimeInterval() {
    this.intervalSubscription = interval(this.getTickTime()).subscribe(() => this.tick());
  }

  private setupEventListeners() {
    this.core.addTimeUpdateListener('d3playbacktimeline', () => {
      this.tick();
    });

    this.pauseSubscription = this.timelineService.pause$.subscribe(() => {
      this.updateTimer(false);
    });

    this.startSubscription = this.timelineService.start$.subscribe(() => {
      this.updateTimer(true);
    });

    const timelineTimeSubscription = this.timelineService.timelineTime$.subscribe((date: Date) => {
      this.goToTime(date);
    });

    this.subscriptions.push(timelineTimeSubscription);
  }

  PlayPause(state: boolean) {
    this.updateTimer(state);
  }

  GoToStart() {
    this.goToStart();
  }

  GoToEnd() {
    this.goToEnd();
  }

  Rewind() {
    this.rewind();
  }

  FastForward() {
    this.fastForward();
  }

  updateTimer(state: boolean) {
    this.play = state;
    this.goToStartClicked = state;
    if (state) {
      this.core.startTime();
      if (!this.intervalSubscription || this.intervalSubscription.closed) {
        this.startTimeInterval();
      }
    } else {
      this.core.pauseTime();
      if (this.intervalSubscription) {
        this.intervalSubscription.unsubscribe();
      }
    }
    this.cdr.detectChanges();
  }

  playState(): boolean {
    return this.play;
  }

  formatSpeed(speed: number): string {
    return `${speed}x`;
  }

  zoomInButtonClicked() {
    this.zoomIn();
  }

  zoomOutButtonClicked() {
    this.zoomOut();
  }
  fastForward() {
    if (this.speed < this.MAX_STEPS) {
      this.speed++;
    }
    if (this.speed === 0) {
      this.speed++;
    }
    this.updateInterval();
  }

  rewind() {
    if (this.speed > -this.MAX_STEPS) {
      this.speed--;
    }
    if (this.speed === 0) {
      this.speed--;
    }
    this.updateInterval();
  }

  goToStart() {
    const sessionData = this.sessionOptionsService.getSessionData();
    if (sessionData?.event?.createdAt) {
      const start = moment(sessionData.event.createdAt).toDate();
      this.goToTime(start);
    }
  }

  goToEnd() {
    const sessionData = this.sessionOptionsService.getSessionData();
    if (sessionData?.event?.updatedAt) {
      const end = moment(sessionData.event.updatedAt).toDate();
      this.goToTime(end);
    }
  }

  zoomIn() {
    this.zoomTimelineByStep(1);
  }

  zoomOut() {
    this.zoomTimelineByStep(-1);
  }


  private tick() {
    if (this.isInternalUpdate) return;

    const sessionData = this.sessionOptionsService.getSessionData();
    if (!sessionData?.event) return;

    const currentTime = this.getMiddleTickPosition();

    if (this.isWithinTimeRange(currentTime)) {
      if (this.play && !this.goingToLive) {
        const xd = this.x0.domain();
        const dx = this.getCurrentDx();

        const newDate1 = new Date(Date.parse(xd[0].toString()) + dx);
        xd[0] = newDate1;
        const newDate2 = new Date(Date.parse(xd[1].toString()) + dx);
        xd[1] = newDate2;
        this.x0.domain(xd);

        this.x.domain(this.x0.range().map(x => this.currentTransform.rescaleX(this.x0).invert(x)));
        this.middleMili += dx;
        this.refresh(this.middleMili);
      }
    } else {
      let incidentStartTime = new Date(sessionData.event.createdAt).getTime();
      let incidentEndTime = new Date(sessionData.event.updatedAt).getTime();
      incidentStartTime = Math.floor(incidentStartTime / 1000) * 1000;
      incidentEndTime = Math.floor(incidentEndTime / 1000) * 1000;

      if (sessionData.event.status === 'active') {
        incidentEndTime = Date.now() + 5000;
      }

      if (currentTime < incidentStartTime) {
        const date = moment(new Date(incidentStartTime + 1000)).toDate();
        this.goToTime(date);
        this.play = false;
        this.goToStartClicked = false;
      } else if (currentTime > incidentEndTime) {
        const date = moment(new Date(incidentEndTime)).toDate();
        this.goToTime(date);
        this.play = false;
        this.goToStartClicked = false;
        this.timelineService.playbackComplete$.next(); 
      }
      this.cdr.detectChanges();
    }

    this.isInternalUpdate = true;
    this.refresh(this.middleMili);
    this.isInternalUpdate = false;
  }

  private isWithinTimeRange(time: number): boolean {
    const sessionData = this.sessionOptionsService.getSessionData();
    if (!sessionData?.event) return false;

    let incidentStartTime = new Date(sessionData.event.createdAt).getTime();
    let incidentEndTime = new Date(sessionData.event.updatedAt).getTime();
    incidentStartTime = Math.floor(incidentStartTime / 1000) * 1000;
    incidentEndTime = Math.floor(incidentEndTime / 1000) * 1000;

    if (sessionData.event.status === 'active') {
      incidentEndTime = Date.now() + 5000;
    }
    return time >= incidentStartTime && time <= incidentEndTime;
  }

  private getMiddleTickPosition(): number {
    const ticks = this.timeline.selectAll('g').nodes();
    if (ticks && ticks.length > 1) {
      const firstTickData = d3.select(ticks[0]).datum() as Date;
      const secondTickData = d3.select(ticks[1]).datum() as Date;
      
      if (firstTickData && secondTickData) {
        const firstTimeMili = firstTickData.getTime();
        const secondTimeMili = secondTickData.getTime();
        const firstTimeX = this.getXValueOfTransform((ticks[0] as Element).getAttribute('transform') || '');
        const secondTimeX = this.getXValueOfTransform((ticks[1] as Element).getAttribute('transform') || '');

        const miliDiff = Math.abs(firstTimeMili - secondTimeMili);
        const xDiff = Math.abs(firstTimeX - secondTimeX);
        
        if (xDiff > 0) {
          const miliPerPixel = miliDiff / xDiff;

          let firstTick = ticks[0];
          let firstTickMili = firstTimeMili;
          let firstTickX = firstTimeX;
          
          for (let i = 1; i < ticks.length; i++) {
            const tickData = d3.select(ticks[i]).datum() as Date;
            if (tickData) {
              const mili = tickData.getTime();
              if (mili < firstTickMili) {
                firstTick = ticks[i];
                firstTickMili = mili;
              }
            }
          }
          
          if (firstTickMili !== firstTimeMili) {
            firstTickX = this.getXValueOfTransform((firstTick as Element).getAttribute('transform') || '');
          }
          
          return Math.abs(this.middleTimePixelX - firstTickX) * miliPerPixel + firstTickMili;
        }
      }
    }
    return Date.now();
  }

  private getXValueOfTransform(transform: any): number {
    if (!transform) return 0;
    const regex = /translate\(([-+]?\d*\.?\d+),?.*?\)/;
    const matches = transform.match(regex);
    if (matches && matches[1]) {
      return matches[1];
    }
    return 0;
  }

  private getCurrentDx(): number {
    return this.speed >= 0 ? 1000 : -1000;
  }

  goToTime(date: Date) {
    const xd = this.x0.domain();
    const secondOffset = (date.getTime() - this.getMiddleTickPosition()) / 1000;
    const newDate1 = new Date(Date.parse(xd[0].toString()));
    newDate1.setSeconds(newDate1.getSeconds() + secondOffset);
    xd[0] = newDate1;
    const newDate2 = new Date(Date.parse(xd[1].toString()));
    newDate2.setSeconds(newDate2.getSeconds() + secondOffset);
    xd[1] = newDate2;
    this.x0.domain(xd);

    this.x.domain(this.x0.range().map(x => this.currentTransform.rescaleX(this.x0).invert(x)));
    this.refresh(date.getTime());
  }

  private refresh(time?: number) {
    this.timeline.call(this.xAxis);
    this.calculateTime(time);
    // Only broadcast time change if not an internal update
    if (!this.isInternalUpdate) {
      this.isInternalUpdate = true;
      this.core.setCurrentTimestamp(time ?? this.middleMili);
      this.isInternalUpdate = false;
    }
  }

  private calculateTime(time?: number) {
    if (!time) {
      this.middleMili = this.getMiddleTickPosition();
    } else {
      this.middleMili = time;
    }

    // Only update the UI if not in an internal update
    if (!this.isInternalUpdate && this.goToStartClicked) {
      this.currentDate = moment(this.middleMili).format('MMM Do YYYY  h:mm:ss A');
      this.cdr.detectChanges();
    }
  }

  private zoomTimelineByStep(zoomType: number) {
    const newZoomLevel = this.zoomLevel + zoomType;
    if (newZoomLevel <= this.MAX_ZOOM && newZoomLevel >= this.MIN_ZOOM) {
      this.zoomLevel = newZoomLevel;
      const currentMiddleMilli = new Date(this.middleMili);

      const newTransform = this.currentTransform.scale(Math.pow(2, zoomType));
      this.svgElement.call(this.zoomBehavior.transform, newTransform);

      if (this.goingToLive) {
        currentMiddleMilli.setTime(Date.now());
      }
      this.goToTime(currentMiddleMilli);
    }
  }

  private onMouseWheel(event: WheelEvent) {
    event.preventDefault();
    if (event.deltaY > 0) {
      this.zoomOut();
    } else if (event.deltaY < 0) {
      this.zoomIn();
    }
  }

  private onTouchMove(event: TouchEvent) {
    if (event.touches.length === 1) {
      this.svgElement.call(this.zoomBehavior);
    } else {
      event.stopPropagation();
    }
  }

  private resetTimeline() {
    this.goToStartClicked = false;
    this.currentDate = 'Click Arrow to Start Playback';
    this.cdr.detectChanges();
  }

  private updateInterval() {
    if (this.intervalSubscription) {
      this.intervalSubscription.unsubscribe();
    }
    this.intervalSubscription = interval(this.getTickTime()).subscribe(() => this.tick());
  }

  private getTickTime(): number {
    switch (Math.abs(this.speed)) {
      case 4: return 100;
      case 3: return 250;
      case 2: return 500;
      default: return 1000;
    }
  }
}